import LumaResourceWorkspace, {lumaMoney} from '../../../components/admin/LumaResourceWorkspace';
import AdminTable from '../../../components/admin/AdminTable';
﻿// pages/ConfigCorrespondenciaPage.tsx
import Loading from '../../../components/Loading';
import React, { useState, useEffect, useCallback } from 'react';
import { Edit, Trash2, History, Plus, Notebook } from 'lucide-react';
import {
    fetchCorrespondenciaConfigs,
    CorrespondenciaConfig,
    toggleCorrespondenciaConfigStatus
} from '../../../services/correspondenciaConfigService';
import AddCorrespondencia from '../../../components/config-correspondencia/AddCorrespondencia';
import EditCorrespondencia from '../../../components/config-correspondencia/EditCorrespondencia';
import DeleteCorrespondencia from '../../../components/config-correspondencia/DeleteCorrespondencia';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useModelPermission } from '../../../hooks/useModelPermission';

const ConfigCorrespondenciaPage: React.FC = () => {
    const [configs, setConfigs] = useState<CorrespondenciaConfig[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedConfig, setSelectedConfig] = useState<CorrespondenciaConfig | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();
    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('correspondenciaconfig', userLevelId);

    const loadConfigs = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            if (perm?.can_read) {
                const data = await fetchCorrespondenciaConfigs();
                setConfigs(data);
            } else {
                setConfigs([]);
            }
        } catch (err) {
            setError('Error al cargar las configuraciones');
            /* Browser diagnostics omitted. */
        } finally {
            setLoading(false);
        }
    }, [perm]);

    useEffect(() => {
        if (perm !== null) {
            loadConfigs();
        }
    }, [loadConfigs, perm]);

    const handleRefresh = () => {
        loadConfigs();
    };

    const handleConfigCreated = (newConfig: CorrespondenciaConfig) => {
        setConfigs(prev => [...prev, newConfig]);
    };

    const handleConfigUpdated = (updatedConfig: CorrespondenciaConfig) => {
        setConfigs(prev => prev.map(config =>
            config.id === updatedConfig.id ? updatedConfig : config
        ));
    };

    const handleConfigDeleted = (deletedConfigId: number) => {
        setConfigs(prev => prev.filter(config => config.id !== deletedConfigId));
    };

    const openEditModal = (config: CorrespondenciaConfig) => {
        setSelectedConfig(config);
        setIsEditModalOpen(true);
    };

    const openDeleteModal = (config: CorrespondenciaConfig) => {
        setSelectedConfig(config);
        setIsDeleteModalOpen(true);
    };

    const handleToggleStatus = async (id: number, currentStatus: boolean) => {
        try {
            await toggleCorrespondenciaConfigStatus(id, !currentStatus);
            await loadConfigs();
        } catch (err) {
            /* Browser diagnostics omitted. */
            alert('Error al cambiar el estado');
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };

    // Función para obtener el nombre completo del servicio y plan
    const getPlanServicioCompleto = (config: CorrespondenciaConfig) => {
        if (config.precio_plan_servicio_info) {
            return `${config.precio_plan_servicio_info.nombre_servicio} - ${config.precio_plan_servicio_info.plan_nombre}`;
        }
        return 'N/A - N/A';
    };

    // Función para obtener el nombre para el modal de eliminar
    const getNombreCompletoParaModal = (config: CorrespondenciaConfig) => {
        if (config.precio_plan_servicio_info) {
            return `${config.precio_plan_servicio_info.nombre_servicio} - ${config.precio_plan_servicio_info.plan_nombre}`;
        }
        return `Configuración #${config.id}`;
    };

    const sortedConfigs = [...configs].sort((a, b) => a.id - b.id);
    const totalPages = Math.ceil(sortedConfigs.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedConfigs = sortedConfigs.slice(startIndex, startIndex + itemsPerPage);

    if (perm === null) return <Loading text="Cargando configuración de correspondencia..." />;

    if (error) {
        return (
            <div className="space-y-6 mx-auto px-4 sm:px-6 lg:px-8 max-w-9xl">
                <div className="flex justify-center items-center h-64">
                    <div className="text-lg text-red-600">{error}</div>
                </div>
            </div>
        );
    }

    return <><LumaResourceWorkspace title="Configuración de correspondencia" description="Consulta y gestiona la información de tu coworking." group="Correspondencia" rows={configs} columns={[{key:'id',label:'Configuración'},{key:'precio_plan_servicio_info',label:'Plan y servicio',render:r=>getNombreCompletoParaModal(r)},{key:'descripcion',label:'Descripción'},{key:'estado',label:'Estado'}]} idKey="id" nameKey="id" statusKey="estado" canRead={perm?.can_read} onCreate={perm?.can_create?()=>{setIsModalOpen(true)}:undefined} onEdit={perm?.can_update?r=>{setSelectedConfig(r);setIsEditModalOpen(true);}:undefined} onDelete={perm?.can_delete?r=>{setSelectedConfig(r);setIsDeleteModalOpen(true);}:undefined} history=""/><ExpiredSessionModal
                    isOpen={isSessionExpired}
                    onClose={async () => {
                        setIsSessionExpired(false);
                        await logout();
                    }}
                />{isModalOpen && (<AddCorrespondencia
                        isOpen={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        onCreated={handleConfigCreated}
                    />)}{isEditModalOpen && selectedConfig && (<EditCorrespondencia
                        isOpen={isEditModalOpen}
                        onClose={() => setIsEditModalOpen(false)}
                        onUpdated={handleConfigUpdated}
                        configId={selectedConfig.id}
                    />)}{isDeleteModalOpen && selectedConfig && (<DeleteCorrespondencia
                        isOpen={isDeleteModalOpen}
                        onClose={() => setIsDeleteModalOpen(false)}
                        onDeleted={handleConfigDeleted}
                        configId={selectedConfig.id}
                        configName={getNombreCompletoParaModal(selectedConfig)}
                    />)}</>;
};

export default ConfigCorrespondenciaPage;
